/**
 * DialogFikoCepComponent - Cep Açma (Pocket) Dialog
 *
 * Profesyonel CAM cep açma parametreleri
 * Autodesk Fusion 360 / Mastercam standardında 5-Tab UI
 *
 * 5 Tab (Profesyonel CAM Standardı):
 * - Tab 0: Geometri (Geometry) - Sınır ve ada seçimi
 * - Tab 1: Takım (Tool) - Takım parametreleri
 * - Tab 2: Yükseklikler (Heights) - Clearance/Retract/Feed/Top/Bottom
 * - Tab 3: Geçişler (Passes) - Strateji, hatve, stock to leave
 * - Tab 4: Bağlantılar (Linking) - Giriş/çıkış yöntemleri
 *
 * @see 2D-CAM-PROFESSIONAL-UI-STANDARD.md
 */

import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { Subscription } from 'rxjs';
import { FikoAktiviteCep } from '../FikoAktiviteCep';
import { FikoVektor3D } from '../../../../FikoVektor3d';
import { FikoSecililer } from '../../../../FikoSecililer';
import { occtFaceVertices, faceBrepVertices, occtFaceIslands, occtFaceRef } from '../../../brep/face-cam-extract';
import { feedsSpeeds, MALZEME_KESIM } from '../../../strategies/feeds-speeds';
import { toolpathIstatistik } from '../../../strategies/cam-toolpath-utils';

@Component({
    selector: 'dialog-fiko-cep',
    standalone: true,
    imports: [CommonModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div class="dialog-cep" *ngIf="aktivite" [class.minimized]="minimized">
        <!-- Header -->
        <div class="d-header" (mousedown)="startDrag($event)">
            <div class="d-title">
                <span class="d-icon">◼</span>
                <span>Cep Açma</span>
                <span class="d-subtitle">{{aktivite?.name}}</span>
            </div>
            <div class="d-actions">
                <button class="d-btn" (click)="minimize()" title="Küçült">─</button>
                <button class="d-btn close" (click)="kapa()" title="Kapat">✕</button>
            </div>
        </div>

        <!-- Tabs - Profesyonel CAM 5-Tab Standardı -->
        <div class="d-tabs">
            <button class="tab" [class.active]="aktifTab === 0" (click)="setTab(0)" title="Geometry">
                <span class="tab-icon">◱</span>
                <span class="tab-label">Geometri</span>
            </button>
            <button class="tab" [class.active]="aktifTab === 1" (click)="setTab(1)" title="Tool">
                <span class="tab-icon">🔧</span>
                <span class="tab-label">Takım</span>
            </button>
            <button class="tab" [class.active]="aktifTab === 2" (click)="setTab(2)" title="Heights">
                <span class="tab-icon">↕</span>
                <span class="tab-label">Yükseklik</span>
            </button>
            <button class="tab" [class.active]="aktifTab === 3" (click)="setTab(3)" title="Passes">
                <span class="tab-icon">⚙</span>
                <span class="tab-label">Geçişler</span>
            </button>
            <button class="tab" [class.active]="aktifTab === 4" (click)="setTab(4)" title="Linking">
                <span class="tab-icon">↗</span>
                <span class="tab-label">Bağlantı</span>
            </button>
        </div>

        <!-- Tab Content -->
        <div class="d-content">
            <!-- TAB 0: GEOMETRİ (Geometry) -->
            <div class="tab-content" *ngIf="aktifTab === 0">
                <!-- Sınır Seçimi -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">◱</span>
                        <span class="s-title">Cep Sınırı (Boundary)</span>
                    </div>
                    <div class="section-body">
                        <div class="select-btn-group">
                            <button class="select-btn" [class.active]="cepSecili"
                                    [class.selecting]="secimModu === 'cepSinir'"
                                    (click)="secCepSinir()">
                                <span class="btn-icon">⬚</span>
                                <span>{{secimModu === 'cepSinir' ? 'Yüzey Seç...' : 'Sınır Seç'}}</span>
                                <span class="btn-status" *ngIf="cepSecili">✓</span>
                            </button>
                            <button class="select-btn small" (click)="temizleCepSinir()" title="Temizle">
                                <span>🗑</span>
                            </button>
                        </div>
                        <div class="select-hint" *ngIf="secimModu === 'cepSinir'">
                            3D sahnede yüzey seçin. ESC: İptal, Enter: Onayla
                        </div>
                        <div class="info-text" *ngIf="aktivite?.cepSinirNoktalar?.length">
                            {{aktivite?.cepSinirNoktalar?.length}} nokta seçildi
                        </div>
                    </div>
                </div>

                <!-- Ada (Island) Seçimi -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">◻</span>
                        <span class="s-title">Adalar (Islands)</span>
                        <label class="toggle-switch">
                            <input type="checkbox" [(ngModel)]="adaIsleEtkin" (change)="adaToggle()">
                            <span class="slider"></span>
                        </label>
                    </div>
                    <div class="section-body" *ngIf="adaIsleEtkin">
                        <button class="select-btn" [class.selecting]="secimModu === 'ada'"
                                (click)="secAda()">
                            <span class="btn-icon">◻</span>
                            <span>{{secimModu === 'ada' ? 'Yüzey Seç...' : 'Ada Ekle'}}</span>
                        </button>
                        <div class="select-hint" *ngIf="secimModu === 'ada'">
                            Ada olarak işaretlenecek yüzeyi seçin. ESC: Bitir
                        </div>
                        <div class="ada-list" *ngIf="aktivite?.adalar?.length">
                            <div class="ada-item" *ngFor="let ada of aktivite?.adalar; let i = index">
                                <span>Ada {{i + 1}}: {{ada?.length}} nokta</span>
                                <button class="btn-remove" (click)="silAda(i)">✕</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Machining Region Info -->
                <div class="section" *ngIf="cepSecili">
                    <div class="section-header">
                        <span class="s-icon">📐</span>
                        <span class="s-title">İşleme Bölgesi</span>
                    </div>
                    <div class="section-body">
                        <div class="info-grid">
                            <div class="info-item">
                                <span class="info-label">Tip</span>
                                <span class="info-value">Kapalı Kontur</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Alan</span>
                                <span class="info-value cyan">{{hesaplaCepAlani() | number:'1.1-1'}} mm²</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 1: TAKIM (Tool) -->
            <div class="tab-content" *ngIf="aktifTab === 1">
                <!-- Takım Parametreleri -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">🔧</span>
                        <span class="s-title">Takım Geometrisi</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item large" (click)="editValue('takimCap')">
                                <span class="param-label">Takım Çapı (D)</span>
                                <span class="param-value cyan">{{aktivite?.takim?.cap || 10}} mm</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Feeds & Speeds -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">⚡</span>
                        <span class="s-title">Feeds & Speeds</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('devir')">
                                <span class="param-label">Spindle Devir</span>
                                <span class="param-value yellow">{{devir}} rpm</span>
                            </div>
                            <div class="param-item" (click)="editValue('ilerleme')">
                                <span class="param-label">Cutting Feed</span>
                                <span class="param-value green">{{ilerleme}} mm/dk</span>
                            </div>
                        </div>
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('ilerlemeDalma')">
                                <span class="param-label">Plunge Feed</span>
                                <span class="param-value">{{ilerlemeDalma}} mm/dk</span>
                            </div>
                            <div class="param-item" (click)="editValue('hizRapid')">
                                <span class="param-label">Rapid Feed</span>
                                <span class="param-value red">{{hizRapid}} mm/dk</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Coolant (Soğutma) -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">💧</span>
                        <span class="s-title">Soğutma (Coolant)</span>
                    </div>
                    <div class="section-body">
                        <div class="btn-group small">
                            <button class="btn-option" [class.active]="sogutma === 'Yok'"
                                    (click)="setSogutma('Yok')">Kapalı</button>
                            <button class="btn-option" [class.active]="sogutma === 'Flood'"
                                    (click)="setSogutma('Flood')">Flood</button>
                            <button class="btn-option" [class.active]="sogutma === 'Mist'"
                                    (click)="setSogutma('Mist')">Mist</button>
                            <button class="btn-option" [class.active]="sogutma === 'Through'"
                                    (click)="setSogutma('Through')">Through</button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 2: YÜKSEKLİKLER (Heights) - PROFESYONEl CAM STANDARDI -->
            <div class="tab-content" *ngIf="aktifTab === 2">
                <!-- Heights Visual Diagram -->
                <div class="section heights-visual">
                    <svg viewBox="0 0 200 160" class="heights-diagram">
                        <!-- Clearance Height Line -->
                        <line x1="10" y1="20" x2="190" y2="20" stroke="#ff5555" stroke-width="1" stroke-dasharray="5,3"/>
                        <text x="195" y="24" fill="#ff5555" font-size="8" text-anchor="end">Clearance</text>

                        <!-- Retract Height Line -->
                        <line x1="10" y1="50" x2="190" y2="50" stroke="#ffaa00" stroke-width="1" stroke-dasharray="3,3"/>
                        <text x="195" y="54" fill="#ffaa00" font-size="8" text-anchor="end">Retract</text>

                        <!-- Feed Height Line -->
                        <line x1="10" y1="70" x2="190" y2="70" stroke="#00ff88" stroke-width="1"/>
                        <text x="195" y="74" fill="#00ff88" font-size="8" text-anchor="end">Feed</text>

                        <!-- Top of Stock -->
                        <rect x="30" y="85" width="140" height="5" fill="#4488ff" opacity="0.5"/>
                        <line x1="30" y1="85" x2="170" y2="85" stroke="#4488ff" stroke-width="2"/>
                        <text x="195" y="89" fill="#4488ff" font-size="8" text-anchor="end">Top</text>

                        <!-- Bottom -->
                        <rect x="50" y="90" width="100" height="50" fill="rgba(0,255,136,0.1)" stroke="#00ff88" stroke-width="1"/>
                        <line x1="50" y1="140" x2="150" y2="140" stroke="#00d4ff" stroke-width="2"/>
                        <text x="195" y="144" fill="#00d4ff" font-size="8" text-anchor="end">Bottom</text>

                        <!-- Tool representation -->
                        <path d="M90,20 L90,130 L100,140 L110,130 L110,20" fill="none" stroke="#888" stroke-width="1" stroke-dasharray="2,2"/>
                    </svg>
                </div>

                <!-- Clearance Height -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon" style="color:#ff5555">━</span>
                        <span class="s-title">Clearance Height</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item large" (click)="editValue('clearanceHeight')">
                                <span class="param-label">Güvenli Hareket Yüksekliği</span>
                                <span class="param-value" style="color:#ff5555">{{clearanceHeight}} mm</span>
                            </div>
                        </div>
                        <div class="info-text">Tüm rapid hareketlerde - fixtures üzerinde güvenli geçiş</div>
                    </div>
                </div>

                <!-- Retract Height -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon" style="color:#ffaa00">┅</span>
                        <span class="s-title">Retract Height</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item large" (click)="editValue('retractHeight')">
                                <span class="param-label">Geri Çekme Yüksekliği</span>
                                <span class="param-value" style="color:#ffaa00">{{retractHeight}} mm</span>
                            </div>
                        </div>
                        <div class="info-text">İşlemler arası geçişlerde - lokal güvenli yükseklik</div>
                    </div>
                </div>

                <!-- Feed Height -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon" style="color:#00ff88">─</span>
                        <span class="s-title">Feed Height</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item large" (click)="editValue('feedHeight')">
                                <span class="param-label">İlerleme Başlangıç Yüksekliği</span>
                                <span class="param-value green">{{feedHeight}} mm</span>
                            </div>
                        </div>
                        <div class="info-text">Rapid'den Feed'e geçiş noktası</div>
                    </div>
                </div>

                <!-- Top of Stock & Bottom -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">↕</span>
                        <span class="s-title">Malzeme Seviyeleri</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('ustZ')">
                                <span class="param-label">Top of Stock</span>
                                <span class="param-value" style="color:#4488ff">{{ustZ}} mm</span>
                            </div>
                            <div class="param-item" (click)="editValue('altZ')">
                                <span class="param-label">Bottom</span>
                                <span class="param-value cyan">{{altZ}} mm</span>
                            </div>
                        </div>
                        <div class="info-text">
                            Toplam Derinlik: <span class="param-value yellow">{{zDerinlik}} mm</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 3: GEÇİŞLER (Passes) -->
            <div class="tab-content" *ngIf="aktifTab === 3">
                <!-- Temizleme Stratejisi -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">⟳</span>
                        <span class="s-title">Clearing Strategy</span>
                    </div>
                    <div class="section-body">
                        <div class="btn-group">
                            <button class="btn-option" [class.active]="strateji === 'Offset'"
                                    (click)="setStrateji('Offset')">
                                <span class="opt-icon">◎</span>
                                <span>Offset</span>
                            </button>
                            <button class="btn-option" [class.active]="strateji === 'Spiral'"
                                    (click)="setStrateji('Spiral')">
                                <span class="opt-icon">🌀</span>
                                <span>Spiral</span>
                            </button>
                            <button class="btn-option" [class.active]="strateji === 'Raster'"
                                    (click)="setStrateji('Raster')">
                                <span class="opt-icon">≡</span>
                                <span>Raster</span>
                            </button>
                            <button class="btn-option" [class.active]="strateji === 'Adaptive'"
                                    (click)="setStrateji('Adaptive')">
                                <span class="opt-icon">⚡</span>
                                <span>Adaptive</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Offset Yönü / Adaptive Optimal Load -->
                <div class="section" *ngIf="strateji === 'Offset' || strateji === 'Adaptive'">
                    <div class="section-body">
                        <div class="btn-group small" *ngIf="strateji === 'Offset'">
                            <button class="btn-option" [class.active]="offsetYonu === 'IctenDisa'"
                                    (click)="setOffsetYonu('IctenDisa')">
                                Inside-Out
                            </button>
                            <button class="btn-option" [class.active]="offsetYonu === 'DistanIce'"
                                    (click)="setOffsetYonu('DistanIce')">
                                Outside-In
                            </button>
                        </div>
                        <div class="param-row" *ngIf="strateji === 'Adaptive'">
                            <div class="param-item" (click)="editValue('optimalYuk')">
                                <span class="param-label">Optimal Load</span>
                                <span class="param-value cyan">{{optimalYuk * 100}}%</span>
                            </div>
                            <div class="info-text">
                                Radial: {{(aktivite?.takim?.cap || 10) * optimalYuk | number:'1.1-1'}} mm
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Raster Açısı -->
                <div class="section" *ngIf="strateji === 'Raster'">
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('rasterAcisi')">
                                <span class="param-label">Raster Angle</span>
                                <span class="param-value">{{rasterAcisi}}°</span>
                            </div>
                            <label class="checkbox-item">
                                <input type="checkbox" [(ngModel)]="rasterCiftYon" (change)="parametreGuncelle()">
                                <span>Zigzag</span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Stepover & Stepdown -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">⇅</span>
                        <span class="s-title">Stepover & Stepdown</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('yanalHatveOrani')">
                                <span class="param-label">Max Stepover</span>
                                <span class="param-value">{{yanalHatveOrani * 100}}%</span>
                            </div>
                            <div class="param-item" (click)="editValue('zHatve')">
                                <span class="param-label">Max Stepdown</span>
                                <span class="param-value cyan">{{zHatve}} mm</span>
                            </div>
                        </div>
                        <div class="info-text">
                            Stepover: {{(aktivite?.takim?.cap || 10) * yanalHatveOrani | number:'1.1-1'}} mm
                            (Ø{{aktivite?.takim?.cap || 10}} × %{{yanalHatveOrani * 100}})
                        </div>
                    </div>
                </div>

                <!-- Stock to Leave -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">📏</span>
                        <span class="s-title">Stock to Leave</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('stockToLeaveWall')">
                                <span class="param-label">Wall (Duvar)</span>
                                <span class="param-value">{{stockToLeaveWall}} mm</span>
                            </div>
                            <div class="param-item" (click)="editValue('stockToLeaveFloor')">
                                <span class="param-label">Floor (Taban)</span>
                                <span class="param-value">{{stockToLeaveFloor}} mm</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Frezeleme Yönü -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">🔄</span>
                        <span class="s-title">Direction</span>
                    </div>
                    <div class="section-body">
                        <div class="btn-group small">
                            <button class="btn-option" [class.active]="frezeYonu === 'Tirmanma'"
                                    (click)="setFrezeYonu('Tirmanma')">
                                Climb (Tırmanma)
                            </button>
                            <button class="btn-option" [class.active]="frezeYonu === 'Konvansiyonel'"
                                    (click)="setFrezeYonu('Konvansiyonel')">
                                Conventional
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Feeds & Speeds (otomatik RPM/ilerleme) -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">⚙️</span>
                        <span class="s-title">Feeds &amp; Speeds</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row" style="gap:6px; align-items:center;">
                            <select [(ngModel)]="camMalzeme" style="flex:1; padding:4px; background:rgba(20,24,32,.9); color:#cdeaff; border:1px solid rgba(0,255,255,.3); border-radius:4px; font-size:11px;">
                                <option *ngFor="let m of malzemeListesi" [value]="m.id">{{m.ad}}</option>
                            </select>
                            <span class="param-label" style="font-size:10px;">Diş</span>
                            <input type="number" min="1" max="12" [(ngModel)]="camDisSayisi" style="width:38px; padding:3px; background:rgba(20,24,32,.9); color:#ffe08a; border:1px solid rgba(0,255,255,.3); border-radius:4px; font-size:11px;">
                            <button (click)="otomatikFeedsSpeeds()" style="padding:4px 8px; background:rgba(0,180,255,.25); color:#cdeaff; border:1px solid rgba(0,255,255,.4); border-radius:4px; font-size:11px; cursor:pointer;">Hesapla</button>
                        </div>
                        <div class="param-row" *ngIf="camStat" style="font-size:10px; color:#9fd7ff; margin-top:4px; justify-content:space-between; align-items:center;">
                            <span>⏱ {{camStat.sure}}</span>
                            <span>↔ {{camStat.mesafe}}m</span>
                            <span>✎ {{camStat.kesme}}m</span>
                            <button (click)="ncIndir()" title="NC (G-code) dosyasını indir" style="padding:3px 7px; background:rgba(0,200,120,.25); color:#bdf; border:1px solid rgba(0,255,160,.4); border-radius:4px; font-size:10px; cursor:pointer;">⬇ NC</button>
                        </div>
                        <div *ngFor="let uy of camUyarilar" style="font-size:10px; color:#ffb86b; margin-top:3px;">⚠ {{uy}}</div>
                    </div>
                </div>

                <!-- G02/G03 Yay Çıktısı (OCCT exact offset) -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">⌒</span>
                        <span class="s-title">G02/G03 Yay (NC)</span>
                        <label class="toggle-switch" title="OCCT exact offset'te yaylar G02/G03 olarak yazılır (kapalı: ince G01)">
                            <input type="checkbox" [(ngModel)]="g02g03Aktif" (change)="parametreGuncelle()">
                            <span class="slider"></span>
                        </label>
                    </div>
                </div>

                <!-- Soğutma (M08/M09) -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">💧</span>
                        <span class="s-title">Soğutma (Flood)</span>
                        <label class="toggle-switch" title="Flood coolant — NC'ye M08 (aç) / M09 (kapat)">
                            <input type="checkbox" [(ngModel)]="sogutmaAktif" (change)="parametreGuncelle()">
                            <span class="slider"></span>
                        </label>
                    </div>
                    <div class="section-body">
                        <div class="param-row" style="align-items:center; gap:8px;">
                            <span class="param-label" style="font-size:10px;">İş Koordinatı (Work Offset)</span>
                            <select [(ngModel)]="workOffset" (change)="parametreGuncelle()" style="padding:3px; background:rgba(20,24,32,.9); color:#cdeaff; border:1px solid rgba(0,255,255,.3); border-radius:4px; font-size:11px;">
                                <option *ngFor="let w of workOffsetList" [value]="w">{{w}}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Finishing Passes -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">✨</span>
                        <span class="s-title">Finishing Passes</span>
                        <label class="toggle-switch">
                            <input type="checkbox" [(ngModel)]="finisEtkin" (change)="parametreGuncelle()">
                            <span class="slider"></span>
                        </label>
                    </div>
                    <div class="section-body" *ngIf="finisEtkin">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('finisGecisSayisi')">
                                <span class="param-label">Number of Passes</span>
                                <span class="param-value">{{finisGecisSayisi}}</span>
                            </div>
                            <div class="param-item" (click)="editValue('finisIlerleme')">
                                <span class="param-label">Finish Feedrate</span>
                                <span class="param-value green">{{finisIlerleme}} mm/dk</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Köşe İşleme -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">⌐</span>
                        <span class="s-title">Corner Control</span>
                    </div>
                    <div class="section-body">
                        <div class="btn-group small">
                            <button class="btn-option" [class.active]="koseIsleme === 'Keskin'"
                                    (click)="setKoseIsleme('Keskin')">Sharp</button>
                            <button class="btn-option" [class.active]="koseIsleme === 'Dairesel'"
                                    (click)="setKoseIsleme('Dairesel')">Rounded</button>
                            <button class="btn-option" [class.active]="koseIsleme === 'Yakinsal'"
                                    (click)="setKoseIsleme('Yakinsal')">Loop</button>
                        </div>
                        <label class="checkbox-item" style="margin-top: 8px;">
                            <input type="checkbox" [(ngModel)]="koseYavaslamaEtkin" (change)="parametreGuncelle()">
                            <span>Corner Slowdown (%{{koseYavaslamaOrani}})</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- TAB 4: BAĞLANTILAR (Linking) -->
            <div class="tab-content" *ngIf="aktifTab === 4">
                <!-- Lead-In (Giriş) -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">↓</span>
                        <span class="s-title">Lead-In (Entry)</span>
                    </div>
                    <div class="section-body">
                        <div class="btn-group">
                            <button class="btn-option" [class.active]="girisYontemi === 'Dikey'"
                                    (click)="setGirisYontemi('Dikey')">
                                <span class="opt-icon">↓</span>
                                <span>Plunge</span>
                            </button>
                            <button class="btn-option" [class.active]="girisYontemi === 'Rampa'"
                                    (click)="setGirisYontemi('Rampa')">
                                <span class="opt-icon">⤵</span>
                                <span>Ramp</span>
                            </button>
                            <button class="btn-option" [class.active]="girisYontemi === 'Helisel'"
                                    (click)="setGirisYontemi('Helisel')">
                                <span class="opt-icon">🌀</span>
                                <span>Helix</span>
                            </button>
                            <button class="btn-option" [class.active]="girisYontemi === 'Yay'"
                                    (click)="setGirisYontemi('Yay')">
                                <span class="opt-icon">↪</span>
                                <span>Arc</span>
                            </button>
                            <button class="btn-option" [class.active]="girisYontemi === 'OnDelme'"
                                    (click)="setGirisYontemi('OnDelme')">
                                <span class="opt-icon">⊙</span>
                                <span>Pre-drill</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Ramping Parameters -->
                <div class="section" *ngIf="girisYontemi === 'Rampa'">
                    <div class="section-header">
                        <span class="s-icon">⤵</span>
                        <span class="s-title">Ramping</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('rampaAcisi')">
                                <span class="param-label">Ramp Angle</span>
                                <span class="param-value">{{rampaAcisi}}°</span>
                            </div>
                        </div>
                        <div class="info-text">Max depth per ramp: {{(10 * Math.tan(rampaAcisi * Math.PI / 180)) | number:'1.1-1'}} mm</div>
                    </div>
                </div>

                <!-- Helical Entry Parameters -->
                <div class="section" *ngIf="girisYontemi === 'Helisel'">
                    <div class="section-header">
                        <span class="s-icon">🌀</span>
                        <span class="s-title">Helical Entry</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('heliselCap')">
                                <span class="param-label">Helix Diameter</span>
                                <span class="param-value">{{heliselCap}} mm</span>
                            </div>
                            <div class="param-item" (click)="editValue('heliselAdim')">
                                <span class="param-label">Pitch</span>
                                <span class="param-value">{{heliselAdim}} mm/rev</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Arc Entry Parameters -->
                <div class="section" *ngIf="girisYontemi === 'Yay'">
                    <div class="section-header">
                        <span class="s-icon">↪</span>
                        <span class="s-title">Arc Lead-In</span>
                    </div>
                    <div class="section-body">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('yayRadius')">
                                <span class="param-label">Arc Radius</span>
                                <span class="param-value">{{yayRadius}} mm</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Lead-Out (Çıkış) -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">↑</span>
                        <span class="s-title">Lead-Out (Exit)</span>
                    </div>
                    <div class="section-body">
                        <div class="btn-group small">
                            <button class="btn-option" [class.active]="cikisYontemi === 'Dikey'"
                                    (click)="setCikisYontemi('Dikey')">Vertical</button>
                            <button class="btn-option" [class.active]="cikisYontemi === 'Rampa'"
                                    (click)="setCikisYontemi('Rampa')">Ramp</button>
                            <button class="btn-option" [class.active]="cikisYontemi === 'Yay'"
                                    (click)="setCikisYontemi('Yay')">Arc</button>
                        </div>
                    </div>
                </div>

                <!-- Transitions -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">↔</span>
                        <span class="s-title">Transitions</span>
                    </div>
                    <div class="section-body">
                        <div class="btn-group small">
                            <button class="btn-option" [class.active]="gecisTipi === 'Direkt'"
                                    (click)="setGecisTipi('Direkt')">Direct</button>
                            <button class="btn-option" [class.active]="gecisTipi === 'Yumusak'"
                                    (click)="setGecisTipi('Yumusak')">Smooth</button>
                            <button class="btn-option" [class.active]="gecisTipi === 'ZKaldir'"
                                    (click)="setGecisTipi('ZKaldir')">Retract</button>
                        </div>
                    </div>
                </div>

                <!-- Stay-Down / Keep Tool Down -->
                <div class="section">
                    <div class="section-header">
                        <span class="s-icon">⤷</span>
                        <span class="s-title">Keep Tool Down</span>
                        <label class="toggle-switch">
                            <input type="checkbox" [(ngModel)]="stayDownEtkin" (change)="parametreGuncelle()">
                            <span class="slider"></span>
                        </label>
                    </div>
                    <div class="section-body" *ngIf="stayDownEtkin">
                        <div class="param-row">
                            <div class="param-item" (click)="editValue('stayDownMesafe')">
                                <span class="param-label">Max Distance</span>
                                <span class="param-value">{{stayDownMesafe}} mm</span>
                            </div>
                            <div class="param-item" (click)="editValue('kaldirmaMesafesi')">
                                <span class="param-label">Lift Height</span>
                                <span class="param-value">{{kaldirmaMesafesi}} mm</span>
                            </div>
                        </div>
                        <div class="info-text">Tool stays down for transitions under {{stayDownMesafe}}mm</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit Overlay -->
        <div class="edit-overlay" *ngIf="editingField" (click)="cancelEdit()">
            <div class="edit-box" (click)="$event.stopPropagation()">
                <div class="edit-label">{{editLabels[editingField]}}</div>
                <input type="number" class="edit-input" [(ngModel)]="editingValue"
                       (keydown.enter)="saveEdit()" (keydown.escape)="cancelEdit()" autofocus>
                <div class="edit-actions">
                    <button class="btn-edit ok" (click)="saveEdit()">✓</button>
                    <button class="btn-edit" (click)="cancelEdit()">✕</button>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <div class="d-footer">
            <button class="btn ok" (click)="kaydet()">Tamam</button>
            <button class="btn" (click)="hesapla()">Önizle</button>
            <button class="btn" (click)="kapa()">İptal</button>
        </div>
    </div>
    `,
    styles: [`
        .dialog-cep {
            width: 260px;
            background: linear-gradient(180deg, rgba(15,25,35,0.97) 0%, rgba(10,18,28,0.98) 100%);
            border: 1px solid rgba(0,255,136,0.3);
            border-radius: 6px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(0,255,136,0.1);
            font-family: 'Segoe UI', sans-serif;
            font-size: 10px;
            color: #e0e0e0;
            position: relative;
            overflow: hidden;
        }

        .dialog-cep.minimized {
            height: 30px;
        }

        .dialog-cep.minimized .d-tabs,
        .dialog-cep.minimized .d-content,
        .dialog-cep.minimized .d-footer { display: none; }

        .d-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 8px;
            background: linear-gradient(90deg, rgba(0,255,136,0.15) 0%, transparent 100%);
            border-bottom: 1px solid rgba(0,255,136,0.2);
            cursor: move;
        }

        .d-title {
            display: flex;
            align-items: center;
            gap: 5px;
        }

        .d-icon {
            color: #00ff88;
            font-size: 11px;
        }

        .d-title span:nth-child(2) {
            font-weight: 600;
            color: #fff;
        }

        .d-subtitle {
            color: #888;
            font-size: 9px;
        }

        .d-actions {
            display: flex;
            gap: 3px;
        }

        .d-btn {
            width: 18px;
            height: 18px;
            border: none;
            background: rgba(255,255,255,0.05);
            color: #888;
            border-radius: 3px;
            cursor: pointer;
            font-size: 10px;
        }

        .d-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .d-btn.close:hover { background: rgba(255,50,50,0.3); color: #ff5555; }

        .d-tabs {
            display: flex;
            border-bottom: 1px solid rgba(255,255,255,0.1);
        }

        .tab {
            flex: 1;
            padding: 5px 3px;
            border: none;
            background: transparent;
            color: #888;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1px;
            transition: all 0.2s;
        }

        .tab:hover { color: #aaa; background: rgba(255,255,255,0.03); }
        .tab.active {
            color: #00ff88;
            background: rgba(0,255,136,0.1);
            border-bottom: 2px solid #00ff88;
        }

        .tab-icon { font-size: 11px; }
        .tab-label { font-size: 8px; }

        .d-content {
            max-height: 450px;
            overflow-y: auto;
            padding: 5px;
        }

        .tab-content { animation: fadeIn 0.2s; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        .section {
            margin-bottom: 6px;
            background: rgba(255,255,255,0.02);
            border-radius: 5px;
            overflow: hidden;
        }

        .section-header {
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 5px 6px;
            background: rgba(0,255,136,0.05);
            border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .s-icon { color: #00ff88; }
        .s-title { font-weight: 500; flex: 1; }

        .section-body { padding: 6px; }

        .param-row {
            display: flex;
            gap: 5px;
            margin-bottom: 5px;
        }

        .param-row:last-child { margin-bottom: 0; }

        .param-item {
            flex: 1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 4px 6px;
            background: rgba(0,0,0,0.2);
            border-radius: 3px;
            cursor: pointer;
            transition: background 0.2s;
        }

        .param-item:hover { background: rgba(0,255,136,0.1); }
        .param-item.large { flex: none; width: 100%; }

        .param-label { color: #888; font-size: 9px; }
        .param-value { font-weight: 600; }
        .param-value.cyan { color: #00d4ff; }
        .param-value.green { color: #00ff88; }
        .param-value.yellow { color: #ffcc00; }
        .param-value.red { color: #ff5555; }

        .btn-group {
            display: flex;
            gap: 3px;
            flex-wrap: wrap;
        }

        .btn-group.small { gap: 3px; }

        .btn-option {
            flex: 1;
            min-width: 46px;
            padding: 5px 4px;
            border: 1px solid rgba(255,255,255,0.1);
            background: rgba(0,0,0,0.2);
            color: #aaa;
            border-radius: 3px;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 3px;
            font-size: 9px;
            transition: all 0.2s;
        }

        .btn-option:hover {
            border-color: rgba(0,255,136,0.3);
            background: rgba(0,255,136,0.05);
        }

        .btn-option.active {
            border-color: #00ff88;
            background: rgba(0,255,136,0.15);
            color: #00ff88;
        }

        .opt-icon { font-size: 11px; }

        .select-btn-group {
            display: flex;
            gap: 5px;
        }

        .select-btn {
            flex: 1;
            padding: 6px;
            border: 1px dashed rgba(0,255,136,0.3);
            background: rgba(0,255,136,0.05);
            color: #00ff88;
            border-radius: 3px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            transition: all 0.2s;
        }

        .select-btn:hover {
            border-style: solid;
            background: rgba(0,255,136,0.1);
        }

        .select-btn.active {
            border-style: solid;
            background: rgba(0,255,136,0.2);
        }

        .select-btn.small {
            flex: 0;
            padding: 6px 8px;
        }

        .btn-status { color: #00ff88; }

        .info-text {
            margin-top: 4px;
            font-size: 8px;
            color: #666;
            text-align: center;
        }

        .toggle-switch {
            position: relative;
            width: 30px;
            height: 16px;
        }

        .toggle-switch input { display: none; }

        .toggle-switch .slider {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(255,255,255,0.1);
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.2s;
        }

        .toggle-switch .slider:before {
            content: '';
            position: absolute;
            width: 12px;
            height: 12px;
            left: 2px;
            bottom: 2px;
            background: #888;
            border-radius: 50%;
            transition: transform 0.2s, background 0.2s;
        }

        .toggle-switch input:checked + .slider {
            background: rgba(0,255,136,0.3);
        }

        .toggle-switch input:checked + .slider:before {
            transform: translateX(14px);
            background: #00ff88;
        }

        .checkbox-item {
            display: flex;
            align-items: center;
            gap: 5px;
            cursor: pointer;
            font-size: 9px;
            color: #aaa;
        }

        .checkbox-item input[type="checkbox"] {
            width: 12px;
            height: 12px;
            accent-color: #00ff88;
        }

        .ada-list {
            margin-top: 5px;
        }

        .ada-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 4px 6px;
            background: rgba(0,0,0,0.2);
            border-radius: 3px;
            margin-bottom: 3px;
            font-size: 9px;
        }

        .btn-remove {
            background: none;
            border: none;
            color: #ff5555;
            cursor: pointer;
            padding: 2px 4px;
        }

        .select-btn.selecting {
            animation: pulse-select 1.5s infinite;
            border-color: #00ff88;
            box-shadow: 0 0 10px rgba(0,255,136,0.5);
        }

        @keyframes pulse-select {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }

        .select-hint {
            margin-top: 5px;
            padding: 4px 6px;
            background: rgba(0,255,136,0.1);
            border: 1px solid rgba(0,255,136,0.3);
            border-radius: 3px;
            font-size: 8px;
            color: #00ff88;
            text-align: center;
        }

        .edit-overlay {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 100;
        }

        .edit-box {
            background: rgba(20,30,40,0.98);
            border: 1px solid rgba(0,255,136,0.3);
            border-radius: 6px;
            padding: 12px;
            min-width: 160px;
        }

        .edit-label {
            margin-bottom: 5px;
            color: #888;
            font-size: 9px;
        }

        .edit-input {
            width: 100%;
            padding: 6px;
            background: rgba(0,0,0,0.3);
            border: 1px solid rgba(0,255,136,0.3);
            border-radius: 3px;
            color: #fff;
            font-size: 12px;
            text-align: center;
        }

        .edit-input:focus {
            outline: none;
            border-color: #00ff88;
        }

        .edit-actions {
            display: flex;
            gap: 5px;
            margin-top: 8px;
        }

        .btn-edit {
            flex: 1;
            padding: 5px;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            font-size: 12px;
        }

        .btn-edit.ok {
            background: rgba(0,255,136,0.2);
            color: #00ff88;
        }

        .btn-edit:not(.ok) {
            background: rgba(255,255,255,0.05);
            color: #888;
        }

        .d-footer {
            display: flex;
            gap: 5px;
            padding: 6px 8px;
            border-top: 1px solid rgba(255,255,255,0.1);
        }

        .btn {
            flex: 1;
            padding: 6px;
            border: 1px solid rgba(255,255,255,0.1);
            background: rgba(255,255,255,0.05);
            color: #aaa;
            border-radius: 3px;
            cursor: pointer;
            font-size: 10px;
            transition: all 0.2s;
        }

        .btn:hover {
            background: rgba(255,255,255,0.1);
            color: #fff;
        }

        .btn.ok {
            background: rgba(0,255,136,0.2);
            border-color: rgba(0,255,136,0.3);
            color: #00ff88;
        }

        .btn.ok:hover {
            background: rgba(0,255,136,0.3);
        }

        /* Scrollbar */
        .d-content::-webkit-scrollbar { width: 6px; }
        .d-content::-webkit-scrollbar-track { background: rgba(0,0,0,0.2); }
        .d-content::-webkit-scrollbar-thumb {
            background: rgba(0,255,136,0.3);
            border-radius: 3px;
        }

        /* Heights Visual Diagram */
        .heights-visual {
            padding: 0;
            margin-bottom: 6px;
        }

        .heights-diagram {
            width: 100%;
            height: 90px;
            background: rgba(0,0,0,0.3);
            border-radius: 5px;
        }

        /* Info Grid (for Geometry tab) */
        .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 5px;
        }

        .info-item {
            display: flex;
            flex-direction: column;
            padding: 4px 6px;
            background: rgba(0,0,0,0.2);
            border-radius: 3px;
        }

        .info-label {
            font-size: 8px;
            color: #666;
            margin-bottom: 2px;
        }

        .info-value {
            font-weight: 600;
            color: #e0e0e0;
        }

        .info-value.cyan { color: #00d4ff; }
    `]
})
export class DialogFikoCepComponent implements OnInit, OnDestroy {
    @Input() aktivite!: FikoAktiviteCep;
    @Input() pencere: any;
    @Input() anaGosterim: any; // FikoThreeJsComponent referansı
    @Output() kapandi = new EventEmitter<void>();
    @Output() onKaydet = new EventEmitter<FikoAktiviteCep>();
    @Output() onIptal = new EventEmitter<void>();

    // UI State
    aktifTab = 0;
    minimized = false;
    cepSecili = false;
    adaIsleEtkin = true;

    // 3D Seçim State
    secimModu: 'yok' | 'cepSinir' | 'ada' = 'yok';
    private fikoSecililer: FikoSecililer | null = null;
    private secimSubscriptions: Subscription[] = [];
    private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

    // Parametreler - Heights (Profesyonel CAM Standardı)
    ustZ = 0;
    altZ = -10;
    clearanceHeight = 25;
    retractHeight = 5;
    feedHeight = 2;
    zHatve = 2;
    yanalHatveOrani = 0.4;
    optimalYuk = 0.15;  // Adaptive strateji için
    stockToLeaveWall = 0.3;
    stockToLeaveFloor = 0.1;
    sogutma: 'Yok' | 'Flood' | 'Mist' | 'Through' = 'Flood';
    strateji: 'Offset' | 'Spiral' | 'Raster' | 'Adaptive' = 'Offset';
    offsetYonu: 'IctenDisa' | 'DistanIce' = 'IctenDisa';
    rasterAcisi = 0;
    rasterCiftYon = true;
    frezeYonu: 'Tirmanma' | 'Konvansiyonel' = 'Tirmanma';
    ilerleme = 1000;
    ilerlemeDalma = 300;

    // Feeds & Speeds otomatik hesap
    camMalzeme = 'aluminyum';
    camDisSayisi = 2;
    malzemeListesi = Object.entries(MALZEME_KESIM).map(([id, v]) => ({ id, ad: v.ad }));
    hizRapid = 10000;
    devir = 8000;
    finisEtkin = true;
    g02g03Aktif = false; // G02/G03 yay çıktısı (seçenek, sadece OCCT exact offset)
    sogutmaAktif = true; // soğutma sıvısı (M08/M09)
    workOffset = 'G54'; // iş koordinat sistemi
    workOffsetList = ['G54', 'G55', 'G56', 'G57', 'G58', 'G59'];
    finisGecisSayisi = 1;
    finisIlerleme = 800;
    finisDevir = 10000;
    koseIsleme: 'Keskin' | 'Dairesel' | 'Yakinsal' = 'Dairesel';
    koseYavaslamaEtkin = true;
    koseYavaslamaOrani = 50;
    girisYontemi: 'Dikey' | 'Rampa' | 'Helisel' | 'Yay' | 'OnDelme' = 'Helisel';
    rampaAcisi = 3;
    heliselCap = 10;
    heliselAdim = 1;
    yayRadius = 5;
    cikisYontemi: 'Dikey' | 'Rampa' | 'Yay' = 'Dikey';
    stayDownEtkin = true;
    stayDownMesafe = 20;
    kaldirmaMesafesi = 2;
    gecisTipi: 'Direkt' | 'Yumusak' | 'ZKaldir' = 'Yumusak';

    // Edit State
    editingField: string | null = null;
    editingValue: number = 0;
    editLabels: Record<string, string> = {
        // Heights
        'ustZ': 'Top of Stock (mm)',
        'altZ': 'Bottom (mm)',
        'clearanceHeight': 'Clearance Height (mm)',
        'retractHeight': 'Retract Height (mm)',
        'feedHeight': 'Feed Height (mm)',
        'zHatve': 'Max Stepdown (mm)',
        // Passes
        'yanalHatveOrani': 'Max Stepover (%)',
        'optimalYuk': 'Optimal Load (%)',
        'rasterAcisi': 'Raster Angle (°)',
        'stockToLeaveWall': 'Wall Stock to Leave (mm)',
        'stockToLeaveFloor': 'Floor Stock to Leave (mm)',
        // Feeds & Speeds
        'ilerleme': 'Cutting Feedrate (mm/min)',
        'ilerlemeDalma': 'Plunge Feedrate (mm/min)',
        'hizRapid': 'Rapid Feedrate (mm/min)',
        'devir': 'Spindle Speed (rpm)',
        // Finishing
        'finisGecisSayisi': 'Finish Passes',
        'finisIlerleme': 'Finish Feedrate (mm/min)',
        'finisDevir': 'Finish Spindle (rpm)',
        // Tool
        'takimCap': 'Tool Diameter (mm)',
        // Linking
        'rampaAcisi': 'Ramp Angle (°)',
        'heliselCap': 'Helix Diameter (mm)',
        'heliselAdim': 'Helix Pitch (mm/rev)',
        'yayRadius': 'Arc Radius (mm)',
        'stayDownMesafe': 'Keep Tool Down Distance (mm)',
        'kaldirmaMesafesi': 'Lift Height (mm)'
    };

    // Math reference for template
    Math = Math;

    // Drag
    private dragOffset = { x: 0, y: 0 };
    private isDragging = false;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        if (!this.aktivite) {
            console.warn('[Cep Dialog] Aktivite bulunamadı!');
            return;
        }
        this.loadFromAktivite();
    }

    get zDerinlik(): number {
        return Math.abs(this.ustZ - this.altZ);
    }

    loadFromAktivite(): void {
        const akt = this.aktivite;
        if (!akt) return;

        // Heights (Z Seviyeleri)
        this.ustZ = akt.ustZ;
        this.altZ = akt.altZ;
        this.clearanceHeight = akt.clearanceHeight ?? 25;
        this.retractHeight = akt.retractHeight ?? 5;
        this.feedHeight = akt.feedHeight ?? 2;
        this.zHatve = akt.zHatve;

        // Passes (Geçişler)
        this.yanalHatveOrani = akt.yanalHatveOrani;
        this.optimalYuk = akt.optimalYuk ?? 0.15;
        this.strateji = akt.temizlemeStratejisi;
        this.offsetYonu = akt.offsetYonu;
        this.rasterAcisi = akt.rasterAcisi;
        this.rasterCiftYon = akt.rasterCiftYon;
        this.frezeYonu = akt.frezeYonu;
        this.stockToLeaveWall = akt.stockToLeaveWall ?? akt.finisPay ?? 0.3;
        this.stockToLeaveFloor = akt.stockToLeaveFloor ?? akt.tabanFinisPay ?? 0.1;

        // Finishing
        this.finisEtkin = akt.finisEtkin;
        this.g02g03Aktif = akt.g02g03Aktif ?? false;
        this.sogutmaAktif = akt.sogutmaAktif ?? true;
        this.workOffset = akt.workOffset ?? 'G54';
        this.finisGecisSayisi = akt.finisGecisSayisi;
        this.finisIlerleme = akt.finisIlerleme;
        this.finisDevir = akt.finisDevir;

        // Corner
        this.koseIsleme = akt.koseIsleme;
        this.koseYavaslamaEtkin = akt.koseYavaslamaEtkin;

        // Feeds & Speeds
        this.ilerleme = akt.ilerleme;
        this.ilerlemeDalma = akt.ilerlemeDalma;
        this.hizRapid = akt.hizRapid;
        this.devir = akt.devir;

        // Linking
        this.girisYontemi = akt.girisYontemi;
        this.rampaAcisi = akt.rampaAcisi;
        this.heliselCap = akt.heliselCap;
        this.heliselAdim = akt.heliselAdim;
        this.yayRadius = akt.yayRadius;
        this.cikisYontemi = akt.cikisYontemi;
        this.stayDownEtkin = akt.stayDownEtkin;
        this.stayDownMesafe = akt.stayDownMesafe;
        this.kaldirmaMesafesi = akt.kaldirmaMesafesi;
        this.gecisTipi = akt.gecisTipi;

        // Geometry
        this.adaIsleEtkin = akt.adaIsleEtkin;
        this.cepSecili = akt.cepSinirNoktalar.length > 0;
    }

    // Tab
    setTab(idx: number) { this.aktifTab = idx; this.cd.detectChanges(); }

    // Strateji
    setStrateji(s: 'Offset' | 'Spiral' | 'Raster' | 'Adaptive') {
        this.strateji = s;
        this.parametreGuncelle();
    }

    setOffsetYonu(y: 'IctenDisa' | 'DistanIce') {
        this.offsetYonu = y;
        this.parametreGuncelle();
    }

    setFrezeYonu(y: 'Tirmanma' | 'Konvansiyonel') {
        this.frezeYonu = y;
        this.parametreGuncelle();
    }

    setKoseIsleme(k: 'Keskin' | 'Dairesel' | 'Yakinsal') {
        this.koseIsleme = k;
        this.parametreGuncelle();
    }

    setGirisYontemi(y: 'Dikey' | 'Rampa' | 'Helisel' | 'Yay' | 'OnDelme') {
        this.girisYontemi = y;
        this.parametreGuncelle();
    }

    setCikisYontemi(y: 'Dikey' | 'Rampa' | 'Yay') {
        this.cikisYontemi = y;
        this.parametreGuncelle();
    }

    setGecisTipi(t: 'Direkt' | 'Yumusak' | 'ZKaldir') {
        this.gecisTipi = t;
        this.parametreGuncelle();
    }

    // ═══════════════════════════════════════════════════════════════════
    // 3D SEÇİM SİSTEMİ - FikoSecililer Entegrasyonu
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Cep sınırı seçimi başlat - Kapalı poligon/yüzey seçimi
     */
    secCepSinir() {
        this.secimModu = 'cepSinir';
        this.baslat3DSecim('face');
        this.cd.detectChanges();
    }

    /**
     * Ada (island) seçimi başlat
     */
    secAda() {
        this.secimModu = 'ada';
        this.baslat3DSecim('face');
        this.cd.detectChanges();
    }

    /**
     * 3D seçim modunu başlat - FikoSecililer kullanarak
     */
    private baslat3DSecim(tip: 'edge' | 'face' | 'vertex'): void {
        const gosterim = this.anaGosterim || this.pencere?.anaGosterim;
        if (!gosterim?.sahneler?.[0]) {
            console.warn('[Cep Dialog] Sahne bulunamadı');
            return;
        }

        // FikoSecililer'i al
        this.fikoSecililer = gosterim.sahneler[0].secimler;
        if (!this.fikoSecililer) {
            console.warn('[Cep Dialog] FikoSecililer bulunamadı');
            return;
        }

        // Önceki subscription'ları temizle
        this.secimSubscriptionTemizle();

        // FikoSecililer'i yapılandır
        this.fikoSecililer.aktif = true;
        this.fikoSecililer.kaliciSecimModu = true;
        this.fikoSecililer.hoverHighlightEnabled = true;
        this.fikoSecililer.temizle();

        // Cep için yüzey/mesh seçimi
        this.fikoSecililer.filitreler = ['Mesh'];

        // Tıklama event'ini dinle
        const tiklandiSub = this.fikoSecililer.tiklandi.subscribe((kesisim: any) => {
            if (kesisim) {
                this.handleSecimKesisim(kesisim);
            }
        });
        this.secimSubscriptions.push(tiklandiSub);

        // Hover event'ini dinle (görsel feedback için)
        const uzerindeSub = this.fikoSecililer.uzerinde.subscribe((kesisim: any) => {
            if (kesisim && gosterim.ciz) {
                gosterim.ciz();
            }
        });
        this.secimSubscriptions.push(uzerindeSub);

        // Keyboard handler (ESC iptal, Enter onayla)
        this.keyDownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.secimModunuKapat();
            } else if (e.key === 'Enter') {
                this.secimOnayla();
            }
        };
        document.addEventListener('keydown', this.keyDownHandler);

        // Cursor değiştir
        const canvas = gosterim?.sunucu?.domElement as HTMLCanvasElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
        }
    }

    /**
     * Seçim kesişimini işle
     */
    /** OCCT gerçek BREP CAM aktif mi (parça STEP shape'inden exact LINE/ARC kontur). */
    occtCamAktif = true;

    private async handleSecimKesisim(kesisim: any): Promise<void> {
        if (!kesisim) return;

        const obj = kesisim.altObje || kesisim.object;
        const point = kesisim.point?.clone() || new THREE.Vector3();
        const faceIndex = kesisim.faceIndex;

        // Sınır çıkarma tier'ları (en doğru → en uyumlu):
        // 1) OCCT GERÇEK BREP (exact LINE/ARC, parça STEP shape'inden) — flag + pick-guard
        // 2) mesh-tabanlı sıralı boundary loop
        // 3) mesh coplanar vertex (geriye dönük)
        let noktalar: FikoVektor3D[] = [];
        let occtAdalar: FikoVektor3D[][] | null = null; // OCCT delik/ada (pocket island otomatik)
        let occtRef: { grup: any; faceId: number } | null = null; // OCCT exact offset referansı

        if (this.occtCamAktif) {
            const occt = await occtFaceVertices(obj, faceIndex, point);
            if (occt && occt.length >= 3) {
                noktalar = occt.map(p => new FikoVektor3D(p.x, p.y, p.z));
                occtRef = occtFaceRef(obj, faceIndex);
                if (this.secimModu === 'cepSinir') {
                    const isl = await occtFaceIslands(obj, faceIndex);
                    if (isl?.length) occtAdalar = isl.map(loop => loop.map(p => new FikoVektor3D(p.x, p.y, p.z)));
                }
            }
        }

        if (noktalar.length < 3) {
            const brep = faceBrepVertices(obj, faceIndex);
            if (brep.length >= 3) noktalar = brep.map(p => new FikoVektor3D(p.x, p.y, p.z));
        }

        if (noktalar.length < 3) {
            const vertices = this.faceVerticesFromMesh(obj, faceIndex);
            if (vertices.length < 3) {
                console.warn('[Cep Dialog] Yetersiz vertex sayısı');
                return;
            }
            noktalar = vertices.map(v => new FikoVektor3D(v.x, v.y, v.z));
        }

        switch (this.secimModu) {
            case 'cepSinir':
                this.handleCepSinirSecim(noktalar, obj);
                if (this.aktivite) {
                    // OCCT exact offset referansı (varsa) — sync OCCT offset için
                    if (occtRef) {
                        obj.updateWorldMatrix(true, false);
                        this.aktivite.occtGrup = occtRef.grup;
                        this.aktivite.occtFaceId = occtRef.faceId;
                        this.aktivite.occtMatris = (obj as THREE.Mesh).matrixWorld.clone();
                    } else {
                        this.aktivite.occtGrup = null;
                        this.aktivite.occtFaceId = null;
                        this.aktivite.occtMatris = null;
                    }
                    // OCCT delikleri otomatik ada olarak ata (pocket island)
                    if (occtAdalar?.length) {
                        this.aktivite.adalar = occtAdalar;
                        console.log(`[OCCT-CAM] ${occtAdalar.length} delik otomatik ada olarak eklendi`);
                    }
                }
                break;
            case 'ada':
                this.handleAdaSecim(noktalar, obj);
                break;
        }

        this.cd.detectChanges();
    }

    /**
     * Cep sınırı seçimini işle
     */
    private handleCepSinirSecim(noktalar: FikoVektor3D[], obj: THREE.Object3D): void {
        const akt = this.aktivite;
        if (!akt) return;

        // Cep sınır noktalarını ayarla
        akt.cepSinirNoktalar = noktalar;
        this.cepSecili = true;

        // Z limitlerini seçilen yüzeyden çıkar
        const zValues = noktalar.map(n => n.z);
        const maxZ = Math.max(...zValues);
        const minZ = Math.min(...zValues);

        // Üst Z = yüzey Z'si, Alt Z = derinlik kadar aşağı
        this.ustZ = maxZ;
        if (this.altZ >= maxZ) {
            this.altZ = maxZ - 10; // Varsayılan derinlik
        }

        this.secimModunuKapat();
    }

    /**
     * Ada seçimini işle
     */
    private handleAdaSecim(noktalar: FikoVektor3D[], obj: THREE.Object3D): void {
        const akt = this.aktivite;
        if (!akt) return;

        // Ada listesine ekle
        if (!akt.adalar) akt.adalar = [];
        akt.adalar.push(noktalar);

        // Seçim modunu devam ettir (birden fazla ada seçilebilir)
        // ESC ile kapatılacak
    }

    /**
     * Mesh'ten face vertex'lerini çıkar
     */
    private faceVerticesFromMesh(obj: THREE.Object3D, faceIndex: number): THREE.Vector3[] {
        const vertices: THREE.Vector3[] = [];

        if (!(obj instanceof THREE.Mesh) || !obj.geometry) {
            return vertices;
        }

        const geometry = obj.geometry;
        const position = geometry.attributes?.position;
        if (!position) return vertices;

        const index = geometry.index;

        // Face'in vertex indekslerini al
        let i0: number, i1: number, i2: number;
        if (index) {
            i0 = index.getX(faceIndex * 3);
            i1 = index.getX(faceIndex * 3 + 1);
            i2 = index.getX(faceIndex * 3 + 2);
        } else {
            i0 = faceIndex * 3;
            i1 = faceIndex * 3 + 1;
            i2 = faceIndex * 3 + 2;
        }

        // Vertex'leri world koordinatlarına dönüştür
        const worldMatrix = obj.matrixWorld;

        const v0 = new THREE.Vector3().fromBufferAttribute(position, i0).applyMatrix4(worldMatrix);
        const v1 = new THREE.Vector3().fromBufferAttribute(position, i1).applyMatrix4(worldMatrix);
        const v2 = new THREE.Vector3().fromBufferAttribute(position, i2).applyMatrix4(worldMatrix);

        vertices.push(v0, v1, v2);

        // Planar yüzey ise etrafındaki vertex'leri de topla (co-planar faces)
        const normal = new THREE.Vector3();
        const edge1 = new THREE.Vector3().subVectors(v1, v0);
        const edge2 = new THREE.Vector3().subVectors(v2, v0);
        normal.crossVectors(edge1, edge2).normalize();

        // Aynı düzlemdeki komşu face'leri bul ve vertex'lerini ekle
        const coplanarVertices = this.findCoplanarVertices(obj, faceIndex, normal, v0);
        if (coplanarVertices.length > 0) {
            vertices.length = 0;
            vertices.push(...coplanarVertices);
        }

        return vertices;
    }

    /**
     * Aynı düzlemdeki komşu yüzeylerin vertex'lerini bul
     */
    private findCoplanarVertices(mesh: THREE.Mesh, startFaceIndex: number, normal: THREE.Vector3, planePoint: THREE.Vector3): THREE.Vector3[] {
        const geometry = mesh.geometry;
        const position = geometry.attributes?.position;
        const index = geometry.index;
        if (!position) return [];

        const worldMatrix = mesh.matrixWorld;
        const threshold = 0.01; // Normal ve mesafe toleransı
        const uniqueVertices = new Map<string, THREE.Vector3>();

        const faceCount = index ? index.count / 3 : position.count / 3;

        for (let f = 0; f < faceCount; f++) {
            let i0: number, i1: number, i2: number;
            if (index) {
                i0 = index.getX(f * 3);
                i1 = index.getX(f * 3 + 1);
                i2 = index.getX(f * 3 + 2);
            } else {
                i0 = f * 3;
                i1 = f * 3 + 1;
                i2 = f * 3 + 2;
            }

            const v0 = new THREE.Vector3().fromBufferAttribute(position, i0).applyMatrix4(worldMatrix);
            const v1 = new THREE.Vector3().fromBufferAttribute(position, i1).applyMatrix4(worldMatrix);
            const v2 = new THREE.Vector3().fromBufferAttribute(position, i2).applyMatrix4(worldMatrix);

            // Face normal hesapla
            const faceNormal = new THREE.Vector3();
            const e1 = new THREE.Vector3().subVectors(v1, v0);
            const e2 = new THREE.Vector3().subVectors(v2, v0);
            faceNormal.crossVectors(e1, e2).normalize();

            // Normal benzerliği kontrol et
            const dotProduct = Math.abs(normal.dot(faceNormal));
            if (dotProduct < 1 - threshold) continue;

            // Düzlem mesafesi kontrol et
            const dist = Math.abs(v0.clone().sub(planePoint).dot(normal));
            if (dist > threshold) continue;

            // Co-planar face - vertex'leri ekle
            const key0 = `${v0.x.toFixed(4)}_${v0.y.toFixed(4)}_${v0.z.toFixed(4)}`;
            const key1 = `${v1.x.toFixed(4)}_${v1.y.toFixed(4)}_${v1.z.toFixed(4)}`;
            const key2 = `${v2.x.toFixed(4)}_${v2.y.toFixed(4)}_${v2.z.toFixed(4)}`;

            if (!uniqueVertices.has(key0)) uniqueVertices.set(key0, v0);
            if (!uniqueVertices.has(key1)) uniqueVertices.set(key1, v1);
            if (!uniqueVertices.has(key2)) uniqueVertices.set(key2, v2);
        }

        // Vertex'leri saat yönünde sırala (convex hull için)
        const vertexArray = Array.from(uniqueVertices.values());
        if (vertexArray.length < 3) return vertexArray;

        return this.sortVerticesClockwise(vertexArray, normal);
    }

    /**
     * Vertex'leri saat yönünde sırala (2D projeksiyon ile)
     */
    private sortVerticesClockwise(vertices: THREE.Vector3[], normal: THREE.Vector3): THREE.Vector3[] {
        if (vertices.length < 3) return vertices;

        // Merkez noktayı hesapla
        const center = new THREE.Vector3();
        vertices.forEach(v => center.add(v));
        center.divideScalar(vertices.length);

        // Normal'e göre projeksiyon eksenleri oluştur
        const up = new THREE.Vector3(0, 0, 1);
        if (Math.abs(normal.dot(up)) > 0.9) {
            up.set(0, 1, 0);
        }
        const xAxis = new THREE.Vector3().crossVectors(up, normal).normalize();
        const yAxis = new THREE.Vector3().crossVectors(normal, xAxis).normalize();

        // Her vertex için açı hesapla ve sırala
        const withAngles = vertices.map(v => {
            const relative = v.clone().sub(center);
            const x = relative.dot(xAxis);
            const y = relative.dot(yAxis);
            const angle = Math.atan2(y, x);
            return { vertex: v, angle };
        });

        withAngles.sort((a, b) => a.angle - b.angle);
        return withAngles.map(item => item.vertex);
    }

    /**
     * Seçimi onayla ve modu kapat
     */
    private secimOnayla(): void {
        this.parametreGuncelle();
        this.secimModunuKapat();
    }

    /**
     * Seçim modunu kapat ve temizle
     */
    private secimModunuKapat(): void {
        const gosterim = this.anaGosterim || this.pencere?.anaGosterim;

        // Keyboard handler'ı kaldır
        if (this.keyDownHandler) {
            document.removeEventListener('keydown', this.keyDownHandler);
            this.keyDownHandler = null;
        }

        // Cursor'u normale döndür
        const canvas = gosterim?.sunucu?.domElement as HTMLCanvasElement;
        if (canvas) {
            canvas.style.cursor = 'default';
        }

        // FikoSecililer'i deaktive et
        if (this.fikoSecililer) {
            this.fikoSecililer.aktif = false;
            this.fikoSecililer.temizle();
            this.fikoSecililer = null;
        }

        // Subscription'ları temizle
        this.secimSubscriptionTemizle();

        this.secimModu = 'yok';
        this.cd.detectChanges();
    }

    /**
     * Subscription'ları temizle
     */
    private secimSubscriptionTemizle(): void {
        this.secimSubscriptions.forEach(sub => {
            if (sub && !sub.closed) {
                sub.unsubscribe();
            }
        });
        this.secimSubscriptions = [];
    }

    temizleCepSinir() {
        const akt = this.aktivite;
        if (akt) {
            akt.cepSinirNoktalar = [];
            this.cepSecili = false;
            this.cd.detectChanges();
        }
    }

    adaToggle() {
        const akt = this.aktivite;
        if (akt) akt.adaIsleEtkin = this.adaIsleEtkin;
        this.cd.detectChanges();
    }

    silAda(idx: number) {
        const akt = this.aktivite;
        if (akt && akt.adalar[idx]) {
            akt.adalar.splice(idx, 1);
            this.cd.detectChanges();
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // LIFECYCLE
    // ═══════════════════════════════════════════════════════════════════

    ngOnDestroy(): void {
        this.secimModunuKapat();
    }

    // Edit
    editValue(field: string) {
        this.editingField = field;
        switch (field) {
            // Heights
            case 'ustZ': this.editingValue = this.ustZ; break;
            case 'altZ': this.editingValue = this.altZ; break;
            case 'clearanceHeight': this.editingValue = this.clearanceHeight; break;
            case 'retractHeight': this.editingValue = this.retractHeight; break;
            case 'feedHeight': this.editingValue = this.feedHeight; break;
            case 'zHatve': this.editingValue = this.zHatve; break;
            // Passes
            case 'yanalHatveOrani': this.editingValue = this.yanalHatveOrani * 100; break;
            case 'optimalYuk': this.editingValue = this.optimalYuk * 100; break;
            case 'rasterAcisi': this.editingValue = this.rasterAcisi; break;
            case 'stockToLeaveWall': this.editingValue = this.stockToLeaveWall; break;
            case 'stockToLeaveFloor': this.editingValue = this.stockToLeaveFloor; break;
            // Feeds & Speeds
            case 'ilerleme': this.editingValue = this.ilerleme; break;
            case 'ilerlemeDalma': this.editingValue = this.ilerlemeDalma; break;
            case 'hizRapid': this.editingValue = this.hizRapid; break;
            case 'devir': this.editingValue = this.devir; break;
            // Finishing
            case 'finisGecisSayisi': this.editingValue = this.finisGecisSayisi; break;
            case 'finisIlerleme': this.editingValue = this.finisIlerleme; break;
            case 'finisDevir': this.editingValue = this.finisDevir; break;
            // Tool
            case 'takimCap': this.editingValue = this.aktivite?.takim?.cap || 10; break;
            // Linking
            case 'rampaAcisi': this.editingValue = this.rampaAcisi; break;
            case 'heliselCap': this.editingValue = this.heliselCap; break;
            case 'heliselAdim': this.editingValue = this.heliselAdim; break;
            case 'yayRadius': this.editingValue = this.yayRadius; break;
            case 'stayDownMesafe': this.editingValue = this.stayDownMesafe; break;
            case 'kaldirmaMesafesi': this.editingValue = this.kaldirmaMesafesi; break;
        }
        this.cd.detectChanges();
    }

    saveEdit() {
        if (!this.editingField) return;
        const val = Number(this.editingValue);
        if (isNaN(val)) {
            this.cancelEdit();
            return;
        }

        switch (this.editingField) {
            // Heights
            case 'ustZ': this.ustZ = val; break;
            case 'altZ': this.altZ = val; break;
            case 'clearanceHeight': this.clearanceHeight = val; break;
            case 'retractHeight': this.retractHeight = val; break;
            case 'feedHeight': this.feedHeight = val; break;
            case 'zHatve': this.zHatve = val; break;
            // Passes
            case 'yanalHatveOrani': this.yanalHatveOrani = val / 100; break;
            case 'optimalYuk': this.optimalYuk = val / 100; break;
            case 'rasterAcisi': this.rasterAcisi = val; break;
            case 'stockToLeaveWall': this.stockToLeaveWall = val; break;
            case 'stockToLeaveFloor': this.stockToLeaveFloor = val; break;
            // Feeds & Speeds
            case 'ilerleme': this.ilerleme = val; break;
            case 'ilerlemeDalma': this.ilerlemeDalma = val; break;
            case 'hizRapid': this.hizRapid = val; break;
            case 'devir': this.devir = val; break;
            // Finishing
            case 'finisGecisSayisi': this.finisGecisSayisi = val; break;
            case 'finisIlerleme': this.finisIlerleme = val; break;
            case 'finisDevir': this.finisDevir = val; break;
            // Tool
            case 'takimCap':
                const akt = this.aktivite;
                if (akt?.takim) akt.takim.cap = val;
                break;
            // Linking
            case 'rampaAcisi': this.rampaAcisi = val; break;
            case 'heliselCap': this.heliselCap = val; break;
            case 'heliselAdim': this.heliselAdim = val; break;
            case 'yayRadius': this.yayRadius = val; break;
            case 'stayDownMesafe': this.stayDownMesafe = val; break;
            case 'kaldirmaMesafesi': this.kaldirmaMesafesi = val; break;
        }

        this.editingField = null;
        this.parametreGuncelle();
    }

    // Soğutma seçimi
    setSogutma(tip: 'Yok' | 'Flood' | 'Mist' | 'Through') {
        this.sogutma = tip;
        this.parametreGuncelle();
    }

    // Cep alanı hesaplama (Shoelace formula)
    hesaplaCepAlani(): number {
        const noktalar = this.aktivite?.cepSinirNoktalar;
        if (!noktalar || noktalar.length < 3) return 0;

        let alan = 0;
        const n = noktalar.length;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            alan += noktalar[i].x * noktalar[j].y;
            alan -= noktalar[j].x * noktalar[i].y;
        }
        return Math.abs(alan / 2);
    }

    cancelEdit() {
        this.editingField = null;
        this.cd.detectChanges();
    }

    /** Parametre güvenlik uyarıları (toolpath'i değiştirmez, sadece uyarır). */
    get camUyarilar(): string[] {
        const u: string[] = [];
        const cap = this.aktivite?.takim?.cap || 0;
        if (this.yanalHatveOrani > 0.75) u.push(`Yanal hatve %${(this.yanalHatveOrani * 100).toFixed(0)} yüksek (>%75) — talaş yükü/takım kırılma riski`);
        if (cap > 0 && this.zHatve > cap) u.push(`Z hatve ${this.zHatve}mm > takım Ø${cap} — dalma derinliği fazla`);
        if (this.devir <= 0) u.push('Spindle hızı 0 — devir girin');
        if (this.ilerleme <= 0) u.push('İlerleme 0');
        const akt = this.aktivite;
        if (akt && akt.adalar?.length > 0 && (akt.temizlemeStratejisi === 'Offset' || akt.temizlemeStratejisi === 'Spiral'))
            u.push(`Adalı cepte ${akt.temizlemeStratejisi} stratejisi ada kaçınma YAPMAZ — takım adaya girebilir! Adaptive strateji kullanın`);
        return u;
    }

    /** NC (G-code) dosyasını üretip indirir (cep aktivitesinin kendi Fanuc çıktısı). */
    ncIndir(): void {
        const akt = this.aktivite;
        if (!akt?.takimYoluNoktalar?.length) { alert('Önce takım yolu hesaplanmalı (yüzey seçin).'); return; }
        const nc = akt.gcodeOlustur();
        const blob = new Blob([nc], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cep-${Date.now()}.nc`;
        a.click();
        URL.revokeObjectURL(url);
    }

    /** Toolpath istatistiği (süre + mesafe) — UI gösterimi. */
    get camStat(): { sure: string; mesafe: string; kesme: string; hareket: number } | null {
        const n = this.aktivite?.takimYoluNoktalar;
        if (!n?.length) return null;
        const s = toolpathIstatistik(n as any, 10000);
        const dk = Math.floor(s.sureSn / 60), sn = Math.round(s.sureSn % 60);
        return {
            sure: dk > 0 ? `${dk}dk ${sn}sn` : `${sn}sn`,
            mesafe: (s.toplamMesafe / 1000).toFixed(2),
            kesme: (s.kesmeMesafe / 1000).toFixed(2),
            hareket: s.hareketSayisi
        };
    }

    /** Malzeme + takım çapı + diş → otomatik RPM/ilerleme (başlangıç değeri; kullanıcı ayarlar). */
    otomatikFeedsSpeeds(): void {
        const cap = this.aktivite?.takim?.cap;
        if (!cap) return;
        const fs = feedsSpeeds(this.camMalzeme, cap, this.camDisSayisi);
        if (!fs) return;
        this.devir = fs.rpm;
        this.ilerleme = fs.ilerleme;
        this.ilerlemeDalma = Math.max(50, Math.round(fs.ilerleme * 0.3)); // dalma ~%30
        this.parametreGuncelle();
        this.cd.detectChanges();
        console.log(`[CAM] F/S: ${this.camMalzeme} D${cap} z${this.camDisSayisi} → ${fs.rpm}rpm ${fs.ilerleme}mm/dk`);
    }

    parametreGuncelle() {
        const akt = this.aktivite;
        if (!akt) return;

        // Heights (Z Seviyeleri - Profesyonel CAM)
        akt.ustZ = this.ustZ;
        akt.altZ = this.altZ;
        akt.clearanceHeight = this.clearanceHeight;
        akt.retractHeight = this.retractHeight;
        akt.feedHeight = this.feedHeight;
        akt.zHatve = this.zHatve;

        // Passes (Geçişler)
        akt.yanalHatveOrani = this.yanalHatveOrani;
        akt.optimalYuk = this.optimalYuk;
        akt.temizlemeStratejisi = this.strateji;
        akt.offsetYonu = this.offsetYonu;
        akt.rasterAcisi = this.rasterAcisi;
        akt.rasterCiftYon = this.rasterCiftYon;
        akt.frezeYonu = this.frezeYonu;
        akt.stockToLeaveWall = this.stockToLeaveWall;
        akt.stockToLeaveFloor = this.stockToLeaveFloor;

        // Finishing
        akt.finisEtkin = this.finisEtkin;
        akt.g02g03Aktif = this.g02g03Aktif;
        akt.sogutmaAktif = this.sogutmaAktif;
        akt.workOffset = this.workOffset;
        akt.finisGecisSayisi = this.finisGecisSayisi;
        akt.finisIlerleme = this.finisIlerleme;
        akt.finisDevir = this.finisDevir;

        // Corner
        akt.koseIsleme = this.koseIsleme;
        akt.koseYavaslamaEtkin = this.koseYavaslamaEtkin;

        // Feeds & Speeds
        akt.ilerleme = this.ilerleme;
        akt.ilerlemeDalma = this.ilerlemeDalma;
        akt.hizRapid = this.hizRapid;
        akt.devir = this.devir;

        // Linking
        akt.girisYontemi = this.girisYontemi;
        akt.rampaAcisi = this.rampaAcisi;
        akt.heliselCap = this.heliselCap;
        akt.heliselAdim = this.heliselAdim;
        akt.yayRadius = this.yayRadius;
        akt.cikisYontemi = this.cikisYontemi;
        akt.stayDownEtkin = this.stayDownEtkin;
        akt.stayDownMesafe = this.stayDownMesafe;
        akt.kaldirmaMesafesi = this.kaldirmaMesafesi;
        akt.gecisTipi = this.gecisTipi;

        // Geometry
        akt.adaIsleEtkin = this.adaIsleEtkin;

        this.cd.detectChanges();
    }

    // Actions
    hesapla() {
        this.parametreGuncelle();
        const akt = this.aktivite;
        if (akt) akt.hesapla();
    }

    kaydet() {
        this.parametreGuncelle();
        this.hesapla();
        this.kapa();
    }

    kapa() {
        this.pencere?.instance?.kapa?.() || this.pencere?.kapa?.();
        this.kapandi.emit();
    }

    // Window
    minimize() { this.minimized = !this.minimized; this.cd.detectChanges(); }

    startDrag(e: MouseEvent) {
        if ((e.target as HTMLElement).closest('.d-btn')) return;
        this.isDragging = true;
        // Drag logic would go here
    }
}
